/**
 * The single death-event read (LOGIN-RELIABILITY-1, s163).
 *
 * SIX components queried `/api/clients/:id/death-event` on the SAME react-query key
 * (`["/api/clients", clientId, "death-event"]`) with six hand-forked queryFns, every one of them
 * swallowing a failed response into `null`:
 *
 *   ExecutorDashboard · ExecutorEstateView · ExecutorPostDeathDashboard · ExecutorProgressView ·
 *   GracePeriodWaiting · executor-plan-edit (useExecutorEditActive)
 *
 * A shared key with forked fetchers means whichever component mounts first populates the cache for
 * all of them — so fixing one fork would have left the defect fully live through the other five.
 * Same hazard as the `estate-checklist` shared-key note in client/CLAUDE.md. One fetcher, one key.
 *
 * `null` is a REAL answer here and must survive: the server returns 200 with a `null` body for a
 * living person (`res.json(event || null)`, server/executor-routes.ts:423). What must NOT survive is
 * a transport failure disguised as that answer — a 502 read as "this person is alive" strands a
 * bereaved executor on the pre-death view until they think to refresh.
 */
import { useQuery } from "@tanstack/react-query";
import { GATING_QUERY_OPTIONS } from "./queryClient";

/** The fields consumers actually read. Declared rather than left as `any` so the de-fork tightens
 *  the six call sites instead of just relocating them; the index signature keeps the rest open. */
export interface DeathEvent {
  id?: string;
  status?: string;
  dateOfDeath?: string | null;
  createdAt?: string | null;
  gracePeriodEndsAt?: string | null;
  gracePeriodHours?: number | null;
  [key: string]: unknown;
}

export const deathEventQueryKey = (clientId: string | undefined) =>
  ["/api/clients", clientId, "death-event"] as const;

/**
 * Fetch the client's active death event, or `null` if there isn't one.
 *
 * Throws on any non-OK response. The alive case never reaches that path — it is a 200 — so there is
 * no 4xx worth mapping to `null`, and anything non-OK is genuinely a failure to report.
 */
export async function fetchDeathEvent(clientId: string): Promise<DeathEvent | null> {
  const res = await fetch(`/api/clients/${clientId}/death-event`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`${res.status}: failed to load death event`);
  }
  return (await res.json()) as DeathEvent | null;
}

interface UseDeathEventOptions {
  enabled?: boolean;
  /** GracePeriodWaiting polls so an externally-verified death lands without a manual refresh. */
  refetchInterval?: number;
}

export function useDeathEvent(clientId: string | undefined, options: UseDeathEventOptions = {}) {
  const { enabled = true, refetchInterval } = options;
  return useQuery<DeathEvent | null>({
    queryKey: deathEventQueryKey(clientId),
    queryFn: () => fetchDeathEvent(clientId as string),
    enabled: enabled && !!clientId,
    ...GATING_QUERY_OPTIONS,
    ...(refetchInterval === undefined ? {} : { refetchInterval }),
  });
}
